import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { Modal, Input, List, Space, Tag, Typography, Spin, Empty, Badge } from "antd";
import {
  SearchOutlined,
  UserOutlined,
  DollarOutlined,
  AppstoreOutlined,
  InboxOutlined,
  MenuOutlined,
  RightOutlined,
  WarningOutlined,
  CheckCircleOutlined,
  WalletOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { api } from "../../api/api";
import { Client, ClientType } from "../../types/client.type";
import { ResidueType } from "../../types/residue.type";
import { Cashbox } from "../../types/cashbox.type";
import { items } from "../../constants/items.constant";
import { useAuth } from "../../contexts/auth.context";
import { useSidebarVisibility } from "../../hooks/useSidebarVisibility";
import { formatNumber } from "../../utils/formatNumber";
import { roundToThreeDecimals } from "../../utils/roundToThreeDecimals";
import "./commandPalette.style.css";

const { Text } = Typography;

interface CommandPaletteProps {
  open: boolean;
  onClose: () => void;
}

type SearchResultType = 
  | { type: 'client'; data: Client; actions: string[] }
  | { type: 'material'; data: ResidueType; actions: string[] }
  | { type: 'produce'; data: ResidueType; actions: string[] }
  | { type: 'cashbox'; data: Cashbox; actions: string[] }
  | { type: 'menu'; data: { key: string; label: string; path: string }; actions: string[] };

export default function CommandPalette({ open, onClose }: CommandPaletteProps) {
  const navigate = useNavigate();
  const { authState } = useAuth();
  const { isItemVisible } = useSidebarVisibility();
  const searchInputRef = useRef<any>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [clients, setClients] = useState<Client[]>([]);
  const [materials, setMaterials] = useState<ResidueType[]>([]);
  const [produces, setProduces] = useState<ResidueType[]>([]);
  const [cashboxes, setCashboxes] = useState<Cashbox[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [mode, setMode] = useState<'search' | 'action'>('search');
  const [selectedItem, setSelectedItem] = useState<SearchResultType | null>(null);

  // Получение доступных пунктов меню для текущего пользователя
  const availableMenuItems = useMemo(() => {
    const userRole = authState.user?.role;
    if (!userRole) return [];

    const flattenMenuItems = (menuItems: typeof items, parentLabel?: string): Array<{ key: string; label: string; path: string }> => {
      let result: Array<{ key: string; label: string; path: string }> = [];
      
      menuItems.forEach(item => {
        if (!item.icon) return;
        
        const hasAccess = item.roles.includes(userRole);
        const isVisible = isItemVisible(item.key);
        
        if (hasAccess && isVisible) {
          const fullLabel = parentLabel ? `${parentLabel} > ${item.label}` : item.label;
          result.push({ key: item.key, label: fullLabel, path: item.key });
        }
        
        if (item.children) {
          const childrenWithAccess = item.children.filter(child => 
            child.roles.includes(userRole) && isItemVisible(child.key)
          );
          if (childrenWithAccess.length > 0) {
            const childResults = flattenMenuItems(childrenWithAccess, item.label);
            result = result.concat(childResults);
          }
        }
      });
      
      return result;
    };

    return flattenMenuItems(items);
  }, [authState.user?.role, isItemVisible]);

  // Поиск по всем источникам
  const searchAll = useCallback(async (query: string) => {
    if (!query.trim()) {
      setClients([]);
      setMaterials([]);
      setProduces([]);
      setCashboxes([]);
      return;
    }

    setLoading(true);
    try {
      const [clientsRes, materialsRes, producesRes, cashboxesRes] = await Promise.all([
        api.get('/client', { params: { search: query, limit: 5 } }),
        api.get('/residue', { params: { search: query, type: 'materials', limit: 5 } }),
        api.get('/residue', { params: { search: query, type: 'produces', limit: 5 } }),
        api.get('/cashbox', { params: { search: query, limit: 5 } }),
      ]);

      setClients(clientsRes.data.clients || []);
      setMaterials(materialsRes.data.data || []);
      setProduces(producesRes.data.data || []);
      setCashboxes(cashboxesRes.data.data || []);
    } catch (error) {
      console.error('Search error:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Дебаунс для поиска
  useEffect(() => {
    const timer = setTimeout(() => {
      searchAll(searchQuery);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery, searchAll]);

  // Сброс состояния при закрытии и автофокус при открытии
  useEffect(() => {
    if (open) {
      // Автофокус на поле поиска при открытии
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
    } else {
      // Сброс при закрытии
      setSearchQuery("");
      setClients([]);
      setMaterials([]);
      setProduces([]);
      setCashboxes([]);
      setSelectedIndex(0);
      setMode('search');
      setSelectedItem(null);
    }
  }, [open]);

  // Фильтрация меню по поиску
  const filteredMenuItems = useMemo(() => {
    if (!searchQuery.trim()) return availableMenuItems.slice(0, 10);
    const query = searchQuery.toLowerCase();
    return availableMenuItems.filter(item => 
      item.label.toLowerCase().includes(query)
    ).slice(0, 10);
  }, [searchQuery, availableMenuItems]);

  // Объединение всех результатов поиска
  const searchResults: SearchResultType[] = useMemo(() => {
    const results: SearchResultType[] = [];

    // Добавляем клиентов
    clients.forEach(client => {
      results.push({
        type: 'client',
        data: client,
        actions: ['orders', 'transactions', 'view']
      });
    });

    // Добавляем материалы
    materials.forEach(material => {
      results.push({
        type: 'material',
        data: material,
        actions: ['batches', 'orders', 'view']
      });
    });

    // Добавляем продукции
    produces.forEach(produce => {
      results.push({
        type: 'produce',
        data: produce,
        actions: ['batches', 'orders', 'view']
      });
    });

    // Добавляем кассы
    cashboxes.forEach(cashbox => {
      results.push({
        type: 'cashbox',
        data: cashbox,
        actions: ['transactions', 'view']
      });
    });

    // Добавляем пункты меню
    filteredMenuItems.forEach(item => {
      results.push({
        type: 'menu',
        data: item,
        actions: ['navigate']
      });
    });

    return results;
  }, [clients, materials, produces, cashboxes, filteredMenuItems]);

  // Обработка навигации
  const handleNavigation = (path: string, state?: any) => {
    navigate(path, { state });
    onClose();
  };

  // Обработка выбора действия
  const handleAction = (result: SearchResultType, action: string) => {
    if (result.type === 'client') {
      const client = result.data as Client;
      switch (action) {
        case 'orders':
          handleNavigation(`/${client._id}/client/${client.clientType}/${client.name}/orders`);
          break;
        case 'transactions':
          handleNavigation(`/${client._id}/client/${client.name}/USD&TJS/transactions`);
          break;
        case 'view':
          const clientType = client.clientType === ClientType.SALE ? 'sale' : client.clientType === ClientType.PURCHASE ? 'purchase' : 'scrap';
          handleNavigation(
            `/clients/${clientType}`,
            { searchQuery: client.name, clientType, forceRefresh: Date.now() }
          );
          break;
      }
    } else if (result.type === 'material') {
      const material = result.data as ResidueType;
      switch (action) {
        case 'batches':
          handleNavigation(`/${material._id}/material/${material.name}/batches`);
          break;
        case 'orders':
          handleNavigation(`/${material._id}/kind/all/${material.name}/orders`);
          break;
        case 'view':
          handleNavigation('/materials', { searchQuery: material.name });
          break;
      }
    } else if (result.type === 'produce') {
      const produce = result.data as ResidueType;
      switch (action) {
        case 'batches':
          handleNavigation(`/${produce._id}/produce/${produce.name}/batches`);
          break;
        case 'orders':
          handleNavigation(`/${produce._id}/kind/all/${produce.name}/orders`);
          break;
        case 'view':
          handleNavigation('/produces', { searchQuery: produce.name });
          break;
      }
    } else if (result.type === 'cashbox') {
      const cashbox = result.data as Cashbox;
      switch (action) {
        case 'transactions':
          handleNavigation(`/${cashbox._id}/cashbox/all/${cashbox.name}/USD&TJS/transactions`);
          break;
        case 'view':
          handleNavigation('/cashboxes', { searchQuery: cashbox.name });
          break;
      }
    } else if (result.type === 'menu') {
      handleNavigation(result.data.path);
    }
  };

  // Обработка клика по элементу
  const handleItemClick = (result: SearchResultType) => {
    if (result.actions.length === 1) {
      handleAction(result, result.actions[0]);
    } else {
      setSelectedItem(result);
      setMode('action');
    }
  };

  // Обработка нажатий клавиш
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (mode === 'action' && selectedItem) {
      if (e.key === 'Escape') {
        setMode('search');
        setSelectedItem(null);
        return;
      }
      
      const actionsCount = selectedItem.actions.length;
      
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex(prev => (prev + 1) % actionsCount);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex(prev => (prev - 1 + actionsCount) % actionsCount);
      } else if (e.key === 'Enter') {
        e.preventDefault();
        handleAction(selectedItem, selectedItem.actions[selectedIndex]);
      }
    } else {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex(prev => (prev + 1) % searchResults.length);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex(prev => (prev - 1 + searchResults.length) % searchResults.length);
      } else if (e.key === 'Enter' && searchResults.length > 0) {
        e.preventDefault();
        handleItemClick(searchResults[selectedIndex]);
      }
    }
  };

  // Рендер иконки в зависимости от типа
  const renderIcon = (result: SearchResultType) => {
    let iconClass = 'result-icon';
    let IconComponent = SearchOutlined;

    switch (result.type) {
      case 'client':
        iconClass += ' icon-client';
        IconComponent = UserOutlined;
        break;
      case 'material':
        iconClass += ' icon-material';
        IconComponent = InboxOutlined;
        break;
      case 'produce':
        iconClass += ' icon-produce';
        IconComponent = AppstoreOutlined;
        break;
      case 'cashbox':
        iconClass += ' icon-cashbox';
        IconComponent = WalletOutlined;
        break;
      case 'menu':
        iconClass += ' icon-menu';
        IconComponent = MenuOutlined;
        break;
    }

    return (
      <div className={iconClass}>
        <IconComponent />
      </div>
    );
  };

  // Рендер информации о статусе (долги, остатки)
  const renderStatusInfo = (result: SearchResultType) => {
    if (result.type === 'client') {
      const client = result.data as Client;
      return (
        <Space size={4} wrap>
          {client.totalClientDebt > 0 && (
            <Tag className="tag-danger" icon={<WarningOutlined />}>
              Долг: {formatNumber(client.totalClientDebt)} {client.debtCurrency}
            </Tag>
          )}
          {client.totalCompanyDebt > 0 && (
            <Tag className="tag-warning">
              Наш долг: {formatNumber(client.totalCompanyDebt)} {client.debtCurrency}
            </Tag>
          )}
          {client.totalClientDebt === 0 && client.totalCompanyDebt === 0 && (
            <Tag className="tag-success" icon={<CheckCircleOutlined />}>
              Без долгов
            </Tag>
          )}
        </Space>
      );
    } else if (result.type === 'material' || result.type === 'produce') {
      const item = result.data as ResidueType;
      const residue = item.residue || 0;
      const lowerLimit = item.lowerLimit || 0;
      const upperLimit = item.upperLimit || 0;
      
      let tagClass = 'tag-info';
      let icon = null;
      
      if (lowerLimit && upperLimit) {
        if (residue < lowerLimit) {
          tagClass = 'tag-danger';
          icon = <WarningOutlined />;
        } else if (residue > upperLimit) {
          tagClass = 'tag-danger';
          icon = <WarningOutlined />;
        } else {
          tagClass = 'tag-success';
          icon = <CheckCircleOutlined />;
        }
      }
      
      return (
        <Space size={4} wrap>
          <Tag className={tagClass} icon={icon}>
            {roundToThreeDecimals(residue)} {item.unitOfMeasurement}
          </Tag>
          {item.averageCost && (
            <Tag className="tag-info">
              {formatNumber(item.averageCost.averageCostUSD)} USD
            </Tag>
          )}
        </Space>
      );
    } else if (result.type === 'cashbox') {
      const cashbox = result.data as Cashbox;
      return (
        <Space size={4} wrap>
          <Tag className="tag-info" icon={<DollarOutlined />}>
            {formatNumber(cashbox.balance)} {cashbox.currency}
          </Tag>
          <Tag className="tag-success">
            +{formatNumber(cashbox.totalInvested)}
          </Tag>
          {cashbox.totalWithdrawn > 0 && (
            <Tag className="tag-warning">
              -{formatNumber(cashbox.totalWithdrawn)}
            </Tag>
          )}
        </Space>
      );
    }
    return null;
  };

  // Рендер описания
  const renderDescription = (result: SearchResultType) => {
    if (result.type === 'client') {
      const client = result.data as Client;
      return (
        <div>
          <Text type="secondary">{client.phone}</Text>
          <br />
          {renderStatusInfo(result)}
        </div>
      );
    } else if (result.type === 'material' || result.type === 'produce') {
      return renderStatusInfo(result);
    } else if (result.type === 'cashbox') {
      const cashbox = result.data as Cashbox;
      return (
        <div>
          <Text type="secondary">Владелец: {cashbox.ownerName}</Text>
          <br />
          {renderStatusInfo(result)}
        </div>
      );
    } else if (result.type === 'menu') {
      return <Text type="secondary">Перейти к разделу</Text>;
    }
    return null;
  };

  // Рендер заголовка
  const renderTitle = (result: SearchResultType) => {
    if (result.type === 'client') {
      const client = result.data as Client;
      return (
        <Space>
          <Text strong>{client.name}</Text>
          <Tag className="tag-info">{client.clientType}</Tag>
        </Space>
      );
    } else if (result.type === 'material') {
      const material = result.data as ResidueType;
      return (
        <Space>
          <Text strong>{material.name}</Text>
          <Tag className="tag-purple">Материал</Tag>
        </Space>
      );
    } else if (result.type === 'produce') {
      const produce = result.data as ResidueType;
      return (
        <Space>
          <Text strong>{produce.name}</Text>
          <Tag className="tag-cyan">Продукция</Tag>
        </Space>
      );
    } else if (result.type === 'cashbox') {
      const cashbox = result.data as Cashbox;
      return (
        <Space>
          <Text strong>{cashbox.name}</Text>
          <Tag className="tag-orange">{cashbox.currency}</Tag>
        </Space>
      );
    } else if (result.type === 'menu') {
      return <Text strong>{result.data.label}</Text>;
    }
    return null;
  };

  // Рендер действий
  const getActionLabel = (result: SearchResultType, action: string): string => {
    if (result.type === 'client') {
      switch (action) {
        case 'orders': return 'Заказы';
        case 'transactions': return 'Транзакции';
        case 'view': return 'Просмотр';
        default: return action;
      }
    } else if (result.type === 'material' || result.type === 'produce') {
      switch (action) {
        case 'batches': return 'Партии';
        case 'orders': return 'Заказы';
        case 'view': return 'Просмотр';
        default: return action;
      }
    } else if (result.type === 'cashbox') {
      switch (action) {
        case 'transactions': return 'Транзакции';
        case 'view': return 'Просмотр';
        default: return action;
      }
    } else if (result.type === 'menu') {
      return 'Перейти';
    }
    return action;
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={700}
      className="command-palette-modal"
      closeIcon={null}
      destroyOnClose
    >
      <div className="command-palette-container">
        <Input
          ref={searchInputRef}
          placeholder={mode === 'action' ? 'Выберите действие...' : 'Поиск клиентов, материалов, продукций, касс, разделов меню...'}
          prefix={<SearchOutlined />}
          value={mode === 'action' ? '' : searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          autoFocus
          size="large"
          className="command-palette-input"
          disabled={mode === 'action'}
        />

        {mode === 'action' && selectedItem && (
          <div className="command-palette-actions">
            <div className="action-header">
              <Text type="secondary">Выберите действие для: {renderTitle(selectedItem)}</Text>
            </div>
            <List
              dataSource={selectedItem.actions}
              renderItem={(action, index) => (
                <List.Item
                  className={`action-item ${index === selectedIndex ? 'selected' : ''}`}
                  onClick={() => handleAction(selectedItem, action)}
                  onMouseEnter={() => setSelectedIndex(index)}
                >
                  <Space>
                    <RightOutlined />
                    <Text strong>{getActionLabel(selectedItem, action)}</Text>
                  </Space>
                </List.Item>
              )}
            />
          </div>
        )}

        {mode === 'search' && (
          <div className="command-palette-results">
            {loading ? (
              <div className="loading-container">
                <Spin size="large" />
              </div>
            ) : searchResults.length === 0 ? (
              <Empty 
                description={searchQuery ? "Ничего не найдено" : "Начните вводить для поиска"} 
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            ) : (
              <List
                dataSource={searchResults}
                renderItem={(result, index) => (
                  <List.Item
                    className={`search-result-item ${index === selectedIndex ? 'selected' : ''}`}
                    onClick={() => handleItemClick(result)}
                    onMouseEnter={() => setSelectedIndex(index)}
                  >
                    <List.Item.Meta
                      avatar={<Badge count={result.actions.length > 1 ? result.actions.length : 0} offset={[-8, 8]}>
                        {renderIcon(result)}
                      </Badge>}
                      title={renderTitle(result)}
                      description={renderDescription(result)}
                    />
                    <RightOutlined style={{ color: '#9ca3af', fontSize: 14 }} />
                  </List.Item>
                )}
              />
            )}
          </div>
        )}

        <div className="command-palette-footer">
          <Space size="large">
            <Text type="secondary" className="footer-hint">
              <Tag>↑↓</Tag> Навигация
            </Text>
            <Text type="secondary" className="footer-hint">
              <Tag>Enter</Tag> Выбрать
            </Text>
            <Text type="secondary" className="footer-hint">
              <Tag>Esc</Tag> Закрыть
            </Text>
          </Space>
        </div>
      </div>
    </Modal>
  );
}

